import { useMemo } from "react";
import useSWRMutation from "swr/mutation";
import useQueryStatusBuilder from "hooks/useQueryStatusBuilder";
import type { Status } from "types/Status";
import type { SWRMutationResponse, MutationFetcher, SWRMutationConfiguration } from "swr/mutation";
import type { Key } from "swr";

export type UseMutationReturn<
  Data = any,
  Error = any,
  SWRMutationKey extends Key = Key,
  ExtraArg = never,
> = SWRMutationResponse<Data, Error, SWRMutationKey, ExtraArg> & {
  status: Status;
};
/**
 * Wrapper around SWR Mutation to provide an additional status value.
 */
function useMutation<Data = any, Error = any, SWRMutationKey extends Key = Key, ExtraArg = never, SWRData = any>(
  key: SWRMutationKey,
  fetcher: MutationFetcher<Data, SWRMutationKey, ExtraArg>,
  config?: SWRMutationConfiguration<Data, Error, SWRMutationKey, ExtraArg, SWRData> & {
    throwOnError?: boolean;
  }
): UseMutationReturn<Data, Error, SWRMutationKey, ExtraArg> {
  const mutation = useSWRMutation<Data, Error, SWRMutationKey, ExtraArg, SWRData>(key, fetcher, config);
  const status = useQueryStatusBuilder({
    isLoading: mutation.isMutating,
    isError: Boolean(mutation.error),
  });

  return useMemo(() => ({ ...mutation, status }), [mutation, status]);
}

export default useMutation;
